/**
 * @fileoverview Form field wrapper component that provides consistent labeling, help text, and validation messaging
 * @module ctl-form-field
 * @version 1.0.0
 * 
 * @description
 * A flexible form field container that wraps form controls to provide consistent structure,
 * labeling, help text, hints, and validation error messages. It automatically handles
 * accessibility attributes (aria-labelledby, aria-describedby, aria-invalid) for both
 * native and custom form controls.
 * 
 * This component is designed to work with any form control including native inputs,
 * textareas, selects, and custom web components. It provides a consistent user experience
 * across all form fields in your application.
 * 
 * @example
 * <!-- Basic text field -->
 * <ctl-form-field label="Email Address">
 *   <input type="email" />
 * </ctl-form-field>
 * 
 * @example
 * <!-- Field with help text and validation -->
 * <ctl-form-field 
 *   label="Password" 
 *   help="Must be at least 8 characters"
 *   required
 *   invalid
 *   error="Password is too short">
 *   <input type="password" />
 * </ctl-form-field>
 * 
 * @example
 * <!-- Field with custom label slot and actions -->
 * <ctl-form-field required>
 *   <span slot="label">Username</span>
 *   <a slot="actions" href="#">Forgot?</a>
 *   <input type="text" />
 * </ctl-form-field>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --ctl-field-gap: var(--ctl-space-1-5);

    display:block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
    color: var(--ctl-color-text);
  }

  .field{
    display:flex;
    flex-direction:column;
    gap: var(--ctl-field-gap);
  }

  .labelRow{
    display:flex;
    align-items:baseline;
    justify-content:space-between;
    gap: var(--ctl-space-3);
  }

  label{
    display:inline-flex;
    align-items:center;
    gap: var(--ctl-space-2);
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-semibold);
    line-height: 1.2;
    color: var(--ctl-color-text);
    cursor: default;
  }

  .requiredDot{
    width: var(--ctl-space-1-5);
    height: var(--ctl-space-1-5);
    border-radius: var(--ctl-radius-full);
    background: var(--ctl-color-danger);
    display:inline-block;
    transform: translateY(-1px);
  }
  :host(:not([required])) .requiredDot{ display:none; }

  .actions{
    display:inline-flex;
    align-items:center;
    gap: var(--ctl-space-2);
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-sm);
  }

  .control{ display:block; }

  .meta{
    display:flex;
    flex-direction:column;
    gap: var(--ctl-space-1);
    font-size: var(--ctl-font-size-sm);
    line-height: 1.3;
  }

  .help, .hint{ color: var(--ctl-color-muted); }
  .error{ color: var(--ctl-color-danger); }

  :host([invalid]) label{ color: var(--ctl-color-danger); }
</style>

<div class="field">
  <div class="labelRow">
    <label id="labelEl">
      <span class="requiredDot" aria-hidden="true"></span>
      <span id="labelText"><slot name="label"></slot></span>
    </label>
    <div class="actions"><slot name="actions"></slot></div>
  </div>

  <div class="control">
    <slot id="controlSlot"></slot>
  </div>

  <div class="meta" id="meta">
    <div class="help" id="helpEl"><slot name="help"></slot></div>
    <div class="help" id="helpTextEl"></div>
    <div class="hint" id="hintEl"></div>
    <div class="error" id="errorEl"><slot name="error"></slot></div>
  </div>
</div>
`;

function uid(prefix = "ctl") {
  return `${prefix}-${Math.random().toString(16).slice(2)}-${Date.now().toString(16)}`;
}

function isFocusable(el) {
  if (!el) return false;
  if (el.hasAttribute?.("disabled")) return false;

  const tag = (el.tagName || "").toLowerCase();
  if (["input", "select", "textarea", "button"].includes(tag)) return true;

  // custom controls usually expose tabindex or focus()
  if (el.hasAttribute?.("tabindex")) return true;
  if (typeof el.focus === "function") return true;

  return false;
}

function isNativeLabelable(el) {
  const tag = (el?.tagName || "").toLowerCase();
  return ["input", "select", "textarea", "button", "meter", "output", "progress"].includes(tag);
}

/**
 * Form Field Web Component
 * 
 * Provides a consistent structure for form controls with labels, help text,
 * and validation messaging. Automatically manages accessibility attributes
 * for proper screen reader support.
 * 
 * @class CtlFormField
 * @extends HTMLElement
 * 
 * @attr {string} label - Field label text (can also use label slot)
 * @attr {string} help - Help text displayed below the control
 * @attr {string} error - Error message displayed when field is invalid
 * @attr {string} hint - Additional hint text displayed below help text
 * @attr {boolean} required - Indicates field is required (shows visual indicator)
 * @attr {boolean} invalid - Marks field as invalid, shows error message
 * 
 * @slot - Default slot for the form control (input, select, textarea, custom element)
 * @slot label - Optional slot for custom label content (overrides label attribute)
 * @slot help - Optional slot for custom help content (overrides help attribute)
 * @slot error - Optional slot for custom error content (overrides error attribute)
 * @slot actions - Slot for action links or buttons next to the label (e.g., "Forgot password?")
 * 
 * @cssprop --ctl-field-gap - Gap between field elements (default: var(--ctl-space-1-5))
 * @cssprop --ctl-font-sans - Font family used for the field content (defaults to system-ui stack)
 * @cssprop --ctl-color-text - Base text color for the field (inherited from theme)
 * @cssprop --ctl-color-muted - Color for help and hint text (inherited from theme)
 * @cssprop --ctl-color-danger - Color used for error and invalid states (inherited from theme)
 */
export class CtlFormField extends HTMLElement {
  /**
   * Observed HTML attributes that trigger attributeChangedCallback
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes() {
    return ["label", "help", "error", "hint", "required", "invalid"];
  }

  /**
   * Create a new CtlFormField instance
   * Initializes shadow DOM and internal element references
   */
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._controlSlot = this.shadowRoot.getElementById("controlSlot");
    this._labelEl = this.shadowRoot.getElementById("labelEl");
    this._labelText = this.shadowRoot.getElementById("labelText");

    this._helpEl = this.shadowRoot.getElementById("helpEl");
    this._helpTextEl = this.shadowRoot.getElementById("helpTextEl");
    this._hintEl = this.shadowRoot.getElementById("hintEl");
    this._errorEl = this.shadowRoot.getElementById("errorEl");
    this._meta = this.shadowRoot.getElementById("meta");

    this._helpId = uid("ctl-help");
    this._hintId = uid("ctl-hint");
    this._errorId = uid("ctl-error");

    this._onSlotChange = this._onSlotChange.bind(this);
    this._onLabelClick = this._onLabelClick.bind(this);
  }

  /**
   * Called when element is added to the DOM
   * Sets up event listeners and initial rendering
   */
  connectedCallback() {
    this._controlSlot.addEventListener("slotchange", this._onSlotChange);
    this._labelEl.addEventListener("click", this._onLabelClick);

    this._render();
    queueMicrotask(() => this._wireControl());
  }

  /**
   * Called when element is removed from the DOM
   * Cleans up event listeners
   */
  disconnectedCallback() {
    this._controlSlot.removeEventListener("slotchange", this._onSlotChange);
    this._labelEl.removeEventListener("click", this._onLabelClick);
  }

  /**
   * Called when observed attributes change
   * Triggers re-render and control re-wiring
   */
  attributeChangedCallback() {
    this._render();
    queueMicrotask(() => this._wireControl());
  }

  _onSlotChange() {
    this._wireControl();
  }

  _onLabelClick() {
    // Critical: clicking label should focus custom controls too
    if (this._control && typeof this._control.focus === "function") {
      this._control.focus();
    }
  }

  _render() {
    const labelAttr = this.getAttribute("label") || "";
    const helpAttr = this.getAttribute("help") || "";
    const hintAttr = this.getAttribute("hint") || "";
    const errorAttr = this.getAttribute("error") || "";
    const invalid = this.hasAttribute("invalid");

    const labelSlot = this.querySelector('[slot="label"]');
    const hasLabelSlot = !!labelSlot;

    // Label
    if (!hasLabelSlot) this._labelText.textContent = labelAttr;

    // Help (slot preferred, attribute fallback)
    const helpSlot = this.querySelector('[slot="help"]');
    this._helpTextEl.textContent = helpSlot ? "" : helpAttr;

    // Hint (attribute only)
    this._hintEl.textContent = hintAttr;

    // Error (slot preferred, attribute fallback)
    const errorSlot = this.querySelector('[slot="error"]');
    if (!errorSlot) this._errorEl.textContent = errorAttr;

    const showHelp = !!(helpSlot || helpAttr);
    const showHint = !!hintAttr;
    const showError = !!(errorSlot || errorAttr);

    // Only show error when invalid
    this._errorEl.style.display = (invalid && showError) ? "block" : "none";
    this._helpEl.style.display = (helpSlot ? "block" : "none"); // slot container
    this._helpTextEl.style.display = (!helpSlot && helpAttr) ? "block" : "none";
    this._hintEl.style.display = showHint ? "block" : "none";

    const metaHasAnything = showHelp || showHint || (invalid && showError);
    this._meta.style.display = metaHasAnything ? "flex" : "none";

    // Hide label row if no label and no actions
    const actionsEl = this.querySelector('[slot="actions"]');
    const hasActions = !!actionsEl && actionsEl.childNodes.length > 0;
    const hasLabelText = hasLabelSlot || labelAttr.trim().length > 0;
    this.shadowRoot.querySelector(".labelRow").style.display = (hasLabelText || hasActions) ? "flex" : "none";
  }

  _wireControl() {
    const assigned = this._controlSlot.assignedElements({ flatten: true }) || [];
    const control = assigned.find(isFocusable) || assigned[0] || null;
    this._control = control;

    // reset label association
    this._labelEl.removeAttribute("for");

    if (!control) return;

    // Ensure stable label id (used for aria-labelledby)
    if (!this._labelEl.id) this._labelEl.id = uid("ctl-label");

    // Ensure control has an id (still useful for native)
    if (!control.id) control.id = uid("ctl-control");

    // Native labelable: use label[for]
    // Custom element: use aria-labelledby and rely on click-to-focus handler above
    if (isNativeLabelable(control)) {
      this._labelEl.setAttribute("for", control.id);
      // remove aria-labelledby if we previously set it
      if (control.getAttribute("aria-labelledby") === this._labelEl.id) {
        control.removeAttribute("aria-labelledby");
      }
    } else {
      control.setAttribute("aria-labelledby", this._labelEl.id);
    }

    // Ensure meta nodes have ids (for aria-describedby)
    this._helpEl.id = this._helpId;       // slot help container
    this._helpTextEl.id = this._helpId;   // same id works because only one is displayed
    this._hintEl.id = this._hintId;
    this._errorEl.id = this._errorId;

    const describedBy = new Set(
      (control.getAttribute("aria-describedby") || "")
        .split(/\s+/)
        .map((s) => s.trim())
        .filter(Boolean)
    );

    // Remove previous managed ids
    describedBy.delete(this._helpId);
    describedBy.delete(this._hintId);
    describedBy.delete(this._errorId);

    const hasHelp = this._helpEl.style.display !== "none" || this._helpTextEl.style.display !== "none";
    const hasHint = this._hintEl.style.display !== "none";
    const hasError = this.hasAttribute("invalid") && this._errorEl.style.display !== "none";

    if (hasHelp) describedBy.add(this._helpId);
    if (hasHint) describedBy.add(this._hintId);
    if (hasError) describedBy.add(this._errorId);

    const next = Array.from(describedBy).join(" ");
    if (next) control.setAttribute("aria-describedby", next);
    else control.removeAttribute("aria-describedby");

    // aria-invalid
    if (this.hasAttribute("invalid")) control.setAttribute("aria-invalid", "true");
    else if (!control.hasAttribute("aria-invalid")) control.removeAttribute("aria-invalid");

    // Forward required (don’t fight consumer if they set it themselves)
    if (this.hasAttribute("required")) {
      if (!control.hasAttribute("required")) control.setAttribute("required", "");
      if (!control.hasAttribute("aria-required")) control.setAttribute("aria-required", "true");
    } else {
      if (control.getAttribute("aria-required") === "true") control.removeAttribute("aria-required");
    }
  }
}

customElements.define("ctl-form-field", CtlFormField);
